'use client';

import { useEffect, useRef, useState } from 'react';
import type { Chart as ChartType, Plugin } from 'chart.js';
import { A2_COLOR, TOOLTIP_STYLE } from './chartTheme';
import ChartHeader from './ChartHeader';
import Logo from '@/components/icons/Logo';

/** Synthesized EVH 5150-style response: real / A2 / A1-with-artifacts. Deterministic, seeded. */
function makeRng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

const N = 700;
const F_MIN = 40;
const F_MAX = 22050;
const FREQS = Array.from(
  { length: N },
  (_, i) => F_MIN * Math.pow(F_MAX / F_MIN, i / (N - 1)),
);

const CTRL: [number, number][] = [
  [40, -43],
  [60, -38],
  [80, -25],
  [100, -9],
  [120, -7],
  [150, -9],
  [200, -13],
  [300, -17],
  [400, -22],
  [500, -23],
  [650, -23],
  [720, -19],
  [780, -14],
  [850, -17],
  [1000, -16],
  [1200, -15],
  [1500, -13],
  [1800, -12],
  [2200, -8],
  [2600, -6],
  [3000, -8],
  [3400, -3],
  [3800, -6],
  [4200, -12],
  [4600, -10],
  [5200, -9],
  [5800, -13],
  [6200, -30],
  [6800, -33],
  [7500, -26],
  [8200, -22],
  [9000, -25],
  [10000, -30],
  [11000, -32],
  [12000, -35],
  [14000, -40],
  [17000, -46],
  [20000, -50],
  [22050, -52],
];

function envelope(f: number) {
  const lf = Math.log(f);
  for (let i = 0; i < CTRL.length - 1; i++) {
    const [f0, d0] = CTRL[i];
    const [f1, d1] = CTRL[i + 1];
    if (f >= f0 && f <= f1) {
      const t = (lf - Math.log(f0)) / (Math.log(f1) - Math.log(f0));
      return d0 + t * (d1 - d0);
    }
  }
  return CTRL[CTRL.length - 1][1];
}

function walk(rng: () => number, n: number, sigma: number, smooth: number) {
  const w = new Array(n).fill(0);
  let v = 0;
  for (let i = 0; i < n; i++) {
    v += (rng() * 2 - 1) * sigma;
    v *= 0.98;
    w[i] = v;
  }
  const out = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    let s = 0;
    let c = 0;
    for (let j = Math.max(0, i - smooth); j <= Math.min(n - 1, i + smooth); j++) {
      s += w[j];
      c++;
    }
    out[i] = s / c;
  }
  return out;
}

function hashAmp(f: number) {
  const t = (Math.log(f) - Math.log(200)) / (Math.log(20000) - Math.log(200));
  return Math.max(0.15, Math.min(1, t)) * 2.2;
}

type Pt = { x: number; y: number };

const DATA = (() => {
  const rngS = makeRng(777);
  const rngR = makeRng(101);
  const rng2 = makeRng(202);
  const rng1 = makeRng(303);

  const shared = walk(rngS, N, 0.35, 3);
  const a2Wander = walk(rng2, N, 0.07, 8);
  const a1Wander = walk(rng1, N, 0.15, 8);

  const real: Pt[] = [];
  const a2: Pt[] = [];
  const a1: Pt[] = [];
  for (let i = 0; i < N; i++) {
    const f = FREQS[i];
    const base = envelope(f) + shared[i];
    const ha = hashAmp(f);
    const realH = ha * (rngR() * 2 - 1);
    real.push({ x: f, y: base + realH });
    a2.push({
      x: f,
      y: base + a2Wander[i] + 0.8 * realH + 0.5 * ha * (rng2() * 2 - 1),
    });

    let v = base + a1Wander[i] + 0.65 * realH;
    const lf = Math.log(f);
    // Abrupt spike, then the hole, around 11 kHz
    v += 9 * Math.exp(-Math.pow((lf - Math.log(10600)) / 0.012, 2));
    v -= 9 * Math.exp(-Math.pow((lf - Math.log(11000)) / 0.018, 2));
    // Above 13 kHz: stops following the downward curve, gets hairy
    let hairMult = 1;
    if (f > 13000) {
      const t = Math.min(1, (lf - Math.log(13000)) / (Math.log(21000) - Math.log(13000)));
      v += t * t * 14;
      hairMult = 1 + t * 3.5;
    }
    v += 0.6 * ha * hairMult * (rng1() * 2 - 1);
    a1.push({ x: f, y: v });
  }
  return { real, a2, a1 };
})();

const WHITE = 'rgba(255,255,255,0.85)';
const YELLOW = A2_COLOR;
const BLUE = 'rgba(90,150,255,1)';

const X_TICKS = [50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];

export default function A2FrequencyResponse() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [includeReal, setIncludeReal] = useState(true);
  const [includeA2, setIncludeA2] = useState(true);
  const [includeA1, setIncludeA1] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let chart: ChartType | undefined;

    (async () => {
      const { Chart, registerables } = await import('chart.js');
      if (cancelled) return;
      Chart.register(...registerables);

      if (!canvasRef.current) return;

      const line = (label: string, data: Pt[], color: string) => ({
        label,
        data,
        borderColor: color,
        backgroundColor: 'transparent',
        borderWidth: 1.2,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
      });

      const datasets = [
        ...(includeReal ? [line('Real amp', DATA.real, WHITE)] : []),
        ...(includeA1 ? [line('NAM A1', DATA.a1, BLUE)] : []),
        ...(includeA2 ? [line('NAM A2', DATA.a2, YELLOW)] : []),
      ];

      const legendPlugin: Plugin<'line'> = {
        id: 'freqLegend',
        afterDatasetsDraw(c) {
          const ctx = c.ctx;
          const { left, top } = c.chartArea;
          ctx.save();
          ctx.font = 'bold 13px Arial, sans-serif';
          ctx.textBaseline = 'middle';
          ctx.textAlign = 'left';
          let y = top + 12;
          c.data.datasets.forEach((ds) => {
            ctx.strokeStyle = typeof ds.borderColor === 'string' ? ds.borderColor : '#fff';
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.moveTo(left + 8, y);
            ctx.lineTo(left + 26, y);
            ctx.stroke();
            ctx.fillStyle = 'rgba(255,255,255,0.85)';
            ctx.fillText(ds.label ?? '', left + 33, y);
            y += 20;
          });
          ctx.restore();
        },
      };

      chart = new Chart(canvasRef.current, {
        type: 'line',
        data: { datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 800, easing: 'easeOutQuart' },
          events: [],
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false, ...TOOLTIP_STYLE },
          },
          scales: {
            x: {
              type: 'logarithmic',
              min: F_MIN,
              max: F_MAX,
              grid: { color: 'rgba(128,128,128,0.15)' },
              ticks: {
                font: { family: 'Arial', size: 13 },
                color: 'rgba(255,255,255,0.85)',
                maxRotation: 0,
                callback: (v) => {
                  const n = typeof v === 'number' ? v : Number(v);
                  if (!X_TICKS.includes(n)) return '';
                  return n >= 1000 ? `${n / 1000}k` : String(n);
                },
              },
              title: {
                display: true,
                text: 'Frequency (Hz)',
                font: { family: 'Arial', size: 14 },
                color: 'rgba(255,255,255,0.9)',
              },
            },
            y: {
              min: -60,
              max: 2,
              grid: { color: 'rgba(128,128,128,0.15)' },
              ticks: {
                stepSize: 10,
                font: { family: 'Arial', size: 13 },
                color: 'rgba(255,255,255,0.85)',
              },
              title: {
                display: true,
                text: 'Magnitude (dB)',
                font: { family: 'Arial', size: 14 },
                color: 'rgba(255,255,255,0.9)',
              },
            },
          },
        },
        plugins: [legendPlugin],
      });
    })();

    return () => {
      cancelled = true;
      chart?.destroy();
    };
  }, [includeReal, includeA2, includeA1]);

  return (
    <div className="w-full border-y border-[#373739] py-4 sm:rounded-2xl sm:border sm:p-8">
      <ChartHeader
        title="Frequency Response vs the Real Amp"
        description="The measured magnitude response of a real amp, compared against its A2 and A1 captures. A2 tracks the full band. A1 stays close, then diverges in the top."
      />
      <div className="w-full rounded-2xl border border-[#373739] p-4 sm:p-6">
        <div className="text-lg font-bold text-white">EVH 5150 Red Channel</div>
        <div className="mb-3 mt-1.5 text-base text-zinc-400">
          Noise-based magnitude response, closer to Real amp is better
        </div>
        <div className="relative h-[420px] w-full max-[500px]:h-[340px]">
          <canvas ref={canvasRef} className="block h-full w-full" />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-3 border-t border-[#373739] pt-4 text-xs">
          <span className="text-zinc-500">Include</span>
          {(
            [
              ['Real amp', includeReal, setIncludeReal],
              ['NAM A2', includeA2, setIncludeA2],
              ['NAM A1', includeA1, setIncludeA1],
            ] as const
          ).map(([label, checked, set]) => (
            <label key={label} className="flex cursor-pointer items-center gap-1.5 text-zinc-300">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => set(e.target.checked)}
                className="accent-[#FFFF00]"
              />
              {label}
            </label>
          ))}
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Logo style={{ height: 20, width: 'auto' }} />
      </div>
    </div>
  );
}
